import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import CalendarHeatmap from '../components/CalendarHeatmap';

const API_BASE = '/api';

// --- Rock display helpers (kept in sync with RocksPage) ---
const CATEGORIES = [
  { value: 'before_the_sale', label: 'Before the Sale', color: 'bg-green-100 text-green-800 border-green-300' },
  { value: 'during_the_sale', label: 'During the Sale', color: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
  { value: 'after_the_sale', label: 'After the Sale', color: 'bg-blue-100 text-blue-800 border-blue-300' },
];

const STATUS_OPTIONS = [
  { value: 'on_track', label: 'On Track', emoji: '🟢' },
  { value: 'off_track', label: 'Off Track', emoji: '🟡' },
  { value: 'stalled', label: 'Stalled', emoji: '🔴' },
];

function getCategoryInfo(cat) {
  return CATEGORIES.find(c => c.value === cat) || { label: cat, color: 'bg-gray-100 text-gray-800 border-gray-300' };
}

function getStatusInfo(status) {
  return STATUS_OPTIONS.find(s => s.value === status) || { label: status, emoji: '⚪' };
}

function getCurrentQuarterLabel() {
  const now = new Date();
  const quarter = Math.ceil((now.getMonth() + 1) / 3);
  return `${now.getFullYear()}-Q${quarter}`;
}

function getCurrentMonth() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

// Shift "YYYY-MM" by N months
function shiftMonth(monthStr, delta) {
  const [year, month] = monthStr.split('-').map(Number);
  const date = new Date(year, month - 1 + delta, 1);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

export default function DashboardPage() {
  const { user, token } = useAuth();
  const [rocks, setRocks] = useState([]);
  const [joinMonth, setJoinMonth] = useState(null); // "YYYY-MM" of signup, caps the back-arrow
  const [scores, setScores] = useState([]);
  const [heatmapMonth, setHeatmapMonth] = useState(getCurrentMonth());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const currentMonth = getCurrentMonth();
  const currentQuarter = getCurrentQuarterLabel();
  const firstName = user?.name ? user.name.split(' ')[0] : '';

  // Initial load: rocks (current quarter) + profile (to learn the signup month).
  const fetchInitial = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [rocksRes, profileRes] = await Promise.all([
        fetch(`${API_BASE}/rocks?quarter=${currentQuarter}`, { headers }),
        fetch(`${API_BASE}/profile`, { headers }),
      ]);
      const rocksData = await rocksRes.json();
      setRocks(rocksData.rocks || []);
      if (profileRes.ok) {
        const p = await profileRes.json();
        const created = p?.profile?.created_at;
        if (created) setJoinMonth(String(created).slice(0, 7)); // "YYYY-MM-DD..." -> "YYYY-MM"
      }
    } catch (err) {
      console.error('Error loading dashboard overview:', err);
      setError('Unable to load your dashboard right now. Please refresh to try again.');
    } finally {
      setLoading(false);
    }
  }, [token, currentQuarter]);

  useEffect(() => {
    fetchInitial();
  }, [fetchInitial]);

  // Heatmap scores: refetch whenever the viewed month changes (arrow nav).
  const fetchScores = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await fetch(`${API_BASE}/scores/monthly?month=${heatmapMonth}`, { headers });
      const data = await res.json();
      setScores(data.scores || []);
    } catch (err) {
      console.error('Error loading heatmap scores:', err);
      setScores([]);
    }
  }, [token, heatmapMonth]);

  useEffect(() => {
    fetchScores();
  }, [fetchScores]);

  // Month-nav bounds: forward never past the current month; back never before signup.
  const canGoNext = heatmapMonth < currentMonth;
  const canGoPrev = !joinMonth || heatmapMonth > joinMonth;
  const goPrevMonth = () => {
    if (canGoPrev) setHeatmapMonth(m => shiftMonth(m, -1));
  };
  const goNextMonth = () => {
    if (canGoNext) setHeatmapMonth(m => shiftMonth(m, 1));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto" data-testid="dashboard-page">
      {/* Welcome header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white">
          Welcome back{firstName ? `, ${firstName}` : ''} 👋
        </h1>
        <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">Here's your overview at a glance.</p>
      </div>

      {/* Primary CTA — log today's activities */}
      <Link
        to="/daily"
        data-testid="log-daily-cta"
        className="flex items-center justify-center gap-2 w-full bg-primary text-white font-semibold text-base px-6 py-4 rounded-lg shadow-sm hover:bg-blue-800 transition-colors mb-6"
      >
        <span className="text-xl">✅</span>
        Log your daily activities now
      </Link>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 mb-6" data-testid="dashboard-error">
          {error}
        </div>
      )}

      {/* Daily Completion Heatmap (scrollable by month, back to signup) */}
      <CalendarHeatmap
        scores={scores}
        monthStr={heatmapMonth}
        onPrevMonth={goPrevMonth}
        onNextMonth={goNextMonth}
        canGoPrev={canGoPrev}
        canGoNext={canGoNext}
      />

      {/* Quarterly Rocks summary */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm mb-6" data-testid="dashboard-rocks-summary">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium text-gray-700 flex items-center gap-2">
            <span>🎯</span> Quarterly Rocks
            <span className="text-xs text-gray-400 font-normal">{currentQuarter}</span>
          </h3>
          <Link to="/rocks" className="text-sm text-primary hover:underline" data-testid="rocks-view-all">
            View all →
          </Link>
        </div>

        {rocks.length === 0 ? (
          <div className="text-center py-6">
            <p className="text-sm text-gray-500 mb-3">No rocks set for this quarter yet.</p>
            <Link
              to="/rocks"
              className="inline-flex items-center gap-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
            >
              Set your quarterly rocks
            </Link>
          </div>
        ) : (
          <div className="space-y-2">
            {rocks.map(rock => {
              const catInfo = getCategoryInfo(rock.category);
              const statusInfo = getStatusInfo(rock.status);
              return (
                <Link
                  to="/rocks"
                  key={rock.id}
                  className="flex items-center justify-between gap-3 p-3 rounded-lg border border-gray-100 hover:bg-gray-50 transition-colors"
                  data-testid={`dashboard-rock-${rock.id}`}
                >
                  <div className="min-w-0">
                    <div className="font-medium text-gray-800 text-sm truncate">{rock.name}</div>
                    <span className={`inline-block mt-1 text-xs px-2 py-0.5 rounded-full border ${catInfo.color}`}>
                      {catInfo.label}
                    </span>
                  </div>
                  <span className="flex items-center gap-1 text-xs font-medium text-gray-600 whitespace-nowrap">
                    <span>{statusInfo.emoji}</span> {statusInfo.label}
                  </span>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
